import { PROJECT_KEY_PATTERN } from '@servicetim/shared';

export const PROJECT_KEY_MAX = 10;

/** Uppercases and strips anything a project key cannot contain, keeping at most 10 characters. */
export const normalizeProjectKey = (raw: string): string =>
  raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, PROJECT_KEY_MAX);

/** Human message for an invalid key, or `null` when it matches `^[A-Z][A-Z0-9]{1,9}$`. */
export function projectKeyProblem(key: string): string | null {
  if (key.length === 0) return 'A key is required';
  if (!/^[A-Z]/.test(key)) return 'The key must start with a letter';
  if (key.length < 2) return 'The key needs at least 2 characters';
  if (!PROJECT_KEY_PATTERN.test(key)) {
    return 'Use 2-10 uppercase letters or digits, starting with a letter';
  }
  return null;
}

export const isValidProjectKey = (key: string): boolean => projectKeyProblem(key) === null;

/**
 * Suggests a key from a project name: initials of up to four words (`IT Service Desk` gives
 * `ISD`), or the first letters of a single word (`Facilities` gives `FACI`). Empty when
 * nothing usable remains.
 */
export function suggestProjectKey(name: string): string {
  const words = name
    .split(/[^A-Za-z0-9]+/)
    .filter((w) => w.length > 0)
    .map((w) => w.toUpperCase());
  if (words.length === 0) return '';
  let candidate: string;
  if (words.length === 1) {
    candidate = (words[0] as string).slice(0, 4);
  } else {
    candidate = words
      .slice(0, 4)
      .map((w) => w[0] as string)
      .join('');
  }
  candidate = candidate.replace(/^[0-9]+/, '');
  if (candidate.length < 2) {
    candidate = words
      .join('')
      .replace(/^[0-9]+/, '')
      .slice(0, 4);
  }
  return normalizeProjectKey(candidate);
}
